import { useState } from "react";
import { useParams, useLocation, useRoute, Link } from "wouter";
import { useAuth } from "@/lib/auth";
import { usePersona, type AudiencePersona } from "@/contexts/persona-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ArrowLeft, Target, Plus } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Navbar from "@/components/navbar";
import PersonaForm from "@/components/persona-form";

export default function PersonaDetailPage() {
  const [isNewRoute] = useRoute("/persona/new");
  const { id } = useParams();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { savedPersonas, setActivePersona, deletePersona, isLoading } = usePersona();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const isNewPersona = isNewRoute || id === 'new';
  const persona = isNewPersona ? undefined : savedPersonas.find(p => p.id === parseInt(id || ''));

  // Redirect to login if not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <div className="container mx-auto px-4 py-16 max-w-4xl text-center">
          <Target className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-neutral-900 mb-4">Sign in Required</h1>
          <p className="text-neutral-600 mb-6">
            Please sign in to create or edit personas.
          </p>
          <Link href="/login">
            <Button>Sign In</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="animate-pulse">
            <div className="h-8 bg-neutral-200 rounded w-32 mb-8" />
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="h-6 bg-neutral-200 rounded w-1/3 mb-4" />
              <div className="h-4 bg-neutral-200 rounded w-2/3 mb-8" />
              <div className="space-y-4">
                <div className="h-10 bg-neutral-200 rounded" />
                <div className="h-10 bg-neutral-200 rounded" />
                <div className="h-10 bg-neutral-200 rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show 404 if persona not found (and not creating new)
  if (!isNewPersona && !persona) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <div className="container mx-auto px-4 py-16 max-w-4xl text-center">
          <Target className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-neutral-900 mb-4">Persona Not Found</h1>
          <p className="text-neutral-600 mb-6">
            The persona you're looking for doesn't exist or has been deleted.
          </p>
          <Link href="/personas">
            <Button variant="outline" className="shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98]">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Personas
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleSave = (savedPersona: AudiencePersona) => {
    if (isNewPersona) {
      // Only activate when creating a new persona
      setActivePersona(savedPersona);
      setLocation(`/persona/${savedPersona.id}`);
    }
    // For edits, just stay on the page - changes are already saved
  };

  const handleDelete = async () => {
    if (persona) {
      await deletePersona(persona.id);
      setLocation('/personas');
    }
  };

  const handleCancel = () => {
    setLocation('/personas');
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />

      {/* Fixed Back Navigation */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-white/90 backdrop-blur-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <Link href="/personas">
            <Button variant="outline" size="sm" className="shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98]">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Personas
            </Button>
          </Link>
        </div>
      </div>

      {/* Spacer for fixed header */}
      <div className="h-16" />

      {/* Form Card */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                {isNewPersona ? (
                  <Plus className="h-6 w-6 text-primary" />
                ) : (
                  <span className="text-2xl">{(persona as any)?.icon || '🎯'}</span>
                )}
              </div>
              <div>
                <CardTitle>
                  {isNewPersona ? 'Create New Persona' : `Edit: ${persona?.profileName}`}
                </CardTitle>
                <CardDescription>
                  {isNewPersona
                    ? 'Define your target audience characteristics to filter and match opportunities'
                    : 'Update the persona details below'
                  }
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <PersonaForm
              initialData={persona}
              onSave={handleSave}
              onCancel={handleCancel}
              showCancelButton
              showDeleteButton={!isNewPersona}
              onDelete={() => setShowDeleteDialog(true)}
              saveButtonText={isNewPersona ? 'Create Persona' : 'Save Changes'}
            />
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Persona</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{persona?.profileName}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
